import type { ButtonHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styles from './Chip.module.css';

interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  variant?: 'filter' | 'category' | 'category-filter' | 'gratis';
  color?: string;
  icon?: React.ReactNode;
  smartDefault?: boolean;
}

/** Map variant strings to CSS module class names */
function getVariantClass(variant: ChipProps['variant']): string {
  switch (variant) {
    case 'filter':
      return styles.filter ?? '';
    case 'category':
      return styles.category ?? '';
    case 'category-filter':
      return styles.categoryFilter ?? '';
    case 'gratis':
      return styles.gratis ?? '';
    default:
      return styles.filter ?? '';
  }
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(
  (
    {
      children,
      selected = false,
      variant = 'filter',
      color,
      icon,
      smartDefault,
      className = '',
      ...props
    },
    ref
  ) => {
    const classNames = [
      styles.chip,
      getVariantClass(variant),
      selected && styles.selected,
      smartDefault && !selected && styles.smartDefault,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const style = color ? ({ '--chip-color': color } as React.CSSProperties) : undefined;

    return (
      <button ref={ref} className={classNames} style={style} {...props}>
        {variant === 'category-filter' && color && (
          <span className={styles.categoryDot} style={{ backgroundColor: color }} />
        )}
        {icon && <span className={styles.icon}>{icon}</span>}
        <span>{children}</span>
      </button>
    );
  }
);

Chip.displayName = 'Chip';
